import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useTransition,
} from 'react';

import NProgress from 'nprogress';
import { usePathname, useRouter } from 'next/navigation';

type NavigateOptions =
  | string
  | {
      pathname: string;
      query?: Record<string, any>;
    };

type RouterContextType = {
  push: (to: NavigateOptions) => void;
  replace: (to: NavigateOptions) => void;
  refresh: () => void;
  back: () => void;
  forward: () => void;
  isLoading: boolean;
  hasPreviousPath?: boolean;
};

const RouterContext = createContext<RouterContextType | undefined>(undefined);

export const RouterProvider = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  const previousPathRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (pathname) {
      previousPathRef.current = pathname;
    }
  }, [pathname]);

  // Start NProgress when navigation begins
  const startNavigation = useCallback(() => {
    NProgress.start();
  }, []);

  // Handle navigation using transition
  const navigate = useCallback(
    (method: 'push' | 'replace', to: NavigateOptions) => {
      startNavigation();

      // Use React's startTransition to track pending state
      startTransition(() => {
        if (typeof to === 'string') {
          router[method](to);
          return;
        }

        const { pathname, query } = to;
        if (query) {
          const searchParams = new URLSearchParams();
          Object.entries(query).forEach(([key, val]) => {
            if (val === null || val === undefined) {
              return;
            }

            if (Array.isArray(val)) {
              val.forEach((item) => {
                if (item !== null && item !== undefined && item !== '') {
                  searchParams.append(key, String(item));
                }
              });
              return;
            }

            if (val !== '') {
              searchParams.set(key, String(val));
            }
          });
          const queryString = searchParams.toString();
          router[method](queryString ? `${pathname}?${queryString}` : pathname);
        } else {
          router[method](pathname);
        }
      });
    },
    [router, startNavigation, startTransition]
  );

  // Add new page to history
  const push = useCallback(
    (to: NavigateOptions) => {
      navigate('push', to);
    },
    [navigate]
  );

  // Replace current page in history
  const replace = useCallback(
    (to: NavigateOptions) => {
      navigate('replace', to);
    },
    [navigate]
  );

  // Reload current page
  const refresh = useCallback(() => {
    startNavigation();
    startTransition(() => {
      router.refresh();
    });
  }, [router, startNavigation, startTransition]);

  // Go to previous page
  const back = useCallback(() => {
    startNavigation();
    startTransition(() => {
      router.back();
    });
  }, [router, startNavigation, startTransition]);

  // Go to next page
  const forward = useCallback(() => {
    startNavigation();
    startTransition(() => {
      router.forward();
    });
  }, [router, startNavigation, startTransition]);

  // Finish loading when transition completes or URL changes
  useEffect(() => {
    if (!isPending) {
      NProgress.done();
    }
  }, [isPending]);

  const value = {
    push,
    replace,
    refresh,
    back,
    forward,
    isLoading: isPending,
    hasPreviousPath: Boolean(previousPathRef?.current),
  };

  return (
    <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
  );
};

export const useAppRouter = (): RouterContextType => {
  const context = useContext(RouterContext);
  if (context === undefined) {
    throw new Error('useAppRouter must be used within a RouterProvider');
  }
  return context;
};
